import { createSlice, createAsyncThunk, PayloadAction, SerializedError, miniSerializeError } from '@reduxjs/toolkit';
import { resetStore } from 'src/store';

import { ServerErrorAdapter } from '@itm/shared-frontend/lib/utils';

import { getCompanyListAll } from 'src/api/employerHub/company';
import { getUploadTemplateIsDbForAllSchemes } from 'src/api/employerHub/scheme';
import { getEmployerAdminPlatformSummary } from 'src/api/employerHub/employer';

import { CompanyResponse, ServerError } from 'src/types';

type CompanyState = {
  selectedCompanyId: string;
  allCompanies: CompanyResponse[];
  isCompanyEmployersPayrollRequired: boolean;
  isPayrollReferenceMigrationAvailable: boolean;
  isLoading: boolean;
  isLoadingIsCompanyEmployersPayrollRequired: boolean;
  isLoadingPayrollReferenceMigrationAvailable: boolean;
  serverErrorMessages: string[];
};

const serializeError = (e: unknown): SerializedError => {
  const message = new ServerErrorAdapter(e as ServerError).combine().join('\n  ');
  return miniSerializeError({ message });
};

export const fetchCompaniesAllAction = createAsyncThunk('company/fetchCompanyListAll', async () => {
  const res = await getCompanyListAll();
  return res.data;
});

type FetchIsCompanyEmployersPayrollRequiredActionParams = { companyId: string; preventLoading?: boolean };

export const fetchIsCompanyEmployersPayrollRequiredAction = createAsyncThunk(
  'company/fetchIsCompanyEmployersPayrollRequiredAction',
  async ({ companyId }: FetchIsCompanyEmployersPayrollRequiredActionParams) => {
    const res = await getUploadTemplateIsDbForAllSchemes(companyId);
    return !res.data;
  },
  { serializeError },
);

type FetchPayrollReferenceMigrationAvailableActionParams = { companyId: string; preventLoading?: boolean };

export const fetchPayrollReferenceMigrationAvailableAction = createAsyncThunk(
  'company/fetchPayrollReferenceMigrationAvailableAction',
  async ({ companyId }: FetchPayrollReferenceMigrationAvailableActionParams) => {
    const res = await getEmployerAdminPlatformSummary(companyId);
    const { hasEmployersWithoutSyncEnabled, hasLumeraCoreEmployers, hasPenScopeEmployers } = res.data;
    return (hasEmployersWithoutSyncEnabled || hasPenScopeEmployers) && !hasLumeraCoreEmployers;
  },
  { serializeError },
);

const initialState: CompanyState = {
  selectedCompanyId: '',
  allCompanies: [],
  isCompanyEmployersPayrollRequired: false,
  isPayrollReferenceMigrationAvailable: false,
  isLoading: false,
  isLoadingIsCompanyEmployersPayrollRequired: false,
  isLoadingPayrollReferenceMigrationAvailable: false,
  serverErrorMessages: [],
};

const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    selectCompany(state: CompanyState, action: PayloadAction<string>) {
      state.selectedCompanyId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCompaniesAllAction.pending, (state: CompanyState) => {
      state.isLoading = true;
    });
    builder.addCase(fetchCompaniesAllAction.fulfilled, (state: CompanyState, action) => {
      state.isLoading = false;
      state.allCompanies = action.payload;
    });
    builder.addCase(fetchCompaniesAllAction.rejected, (state: CompanyState) => {
      state.isLoading = false;
    });

    builder.addCase(fetchIsCompanyEmployersPayrollRequiredAction.pending, (state: CompanyState, action) => {
      if (action.meta.arg.preventLoading) return;
      state.isLoadingIsCompanyEmployersPayrollRequired = true;
    });
    builder.addCase(fetchIsCompanyEmployersPayrollRequiredAction.fulfilled, (state: CompanyState, action) => {
      state.isLoadingIsCompanyEmployersPayrollRequired = false;
      state.isCompanyEmployersPayrollRequired = action.payload;
    });
    builder.addCase(fetchIsCompanyEmployersPayrollRequiredAction.rejected, (state: CompanyState, action) => {
      state.isLoadingIsCompanyEmployersPayrollRequired = false;
      state.serverErrorMessages = action.error.message ? [action.error.message] : [];
    });

    builder.addCase(fetchPayrollReferenceMigrationAvailableAction.pending, (state: CompanyState, action) => {
      if (action.meta.arg.preventLoading) return;
      state.isLoadingPayrollReferenceMigrationAvailable = true;
    });
    builder.addCase(fetchPayrollReferenceMigrationAvailableAction.fulfilled, (state: CompanyState, action) => {
      state.isLoadingPayrollReferenceMigrationAvailable = false;
      state.isPayrollReferenceMigrationAvailable = action.payload;
    });
    builder.addCase(fetchPayrollReferenceMigrationAvailableAction.rejected, (state: CompanyState, action) => {
      state.isLoadingPayrollReferenceMigrationAvailable = false;
      state.serverErrorMessages = action.error.message ? [action.error.message] : [];
    });

    // Cleanup
    builder.addCase(resetStore, (state: CompanyState) => {
      Object.assign(state, initialState);
    });
  },
});

export const { selectCompany } = companySlice.actions;

export default companySlice.reducer;
